import { useState, useEffect, useRef, useContext } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tv, Eye, Globe, Share2, Info, ChevronRight, Cast, CreditCard, Heart, AlertTriangle, WifiOff } from "lucide-react";
import { AlertModal } from "@/components/AlertModal";
import AdSlot from "@/components/AdSlot";
import axios from "axios";
import { API, SettingsContext } from "@/App";
import { trackChannelView, trackPlaybackError } from "@/lib/analytics";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css";
import DOMPurify from "dompurify";

// Register chromecast plugin if available
try {
  require('@silvermine/videojs-chromecast')(videojs);
} catch (e) {
  // Chromecast plugin not available in this environment
}

function getYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/live\/|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/channel\/([a-zA-Z0-9_-]+)\/live/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function isRawIframe(str) {
  return str && str.trim().startsWith('<iframe');
}

function extractIframeSrc(html) {
  const m = html.match(/src=["']([^"']+)["']/);
  return m ? m[1] : null;
}

function isEjaTv(url) {
  return url && url.includes('eja.tv');
}

function isEmbeddable(channel) {
  if (channel.embed_url) return true;
  if (getYouTubeId(channel.stream_url)) return true;
  return false;
}

function getEmbedSrc(channel) {
  const embedStr = channel.embed_url || '';
  
  // Raw iframe HTML - extract src
  if (isRawIframe(embedStr)) {
    return extractIframeSrc(embedStr) || embedStr;
  }
  
  // YouTube URL in embed_url
  const ytId = getYouTubeId(embedStr);
  if (ytId) return `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`;
  
  // eja.tv links - use directly in iframe
  if (isEjaTv(embedStr)) return embedStr;
  
  // Direct embed URL
  if (embedStr) return embedStr;
  
  // Fallback: check stream_url for YouTube
  const ytId2 = getYouTubeId(channel.stream_url);
  if (ytId2) return `https://www.youtube.com/embed/${ytId2}?autoplay=1&rel=0`;
  
  return null;
}

function detectStreamType(url) {
  if (!url) return 'video/mp4';
  const lower = url.split('?')[0].toLowerCase();
  if (lower.endsWith('.m3u8')) return 'application/x-mpegURL';
  if (lower.endsWith('.mpd')) return 'application/dash+xml';
  if (lower.endsWith('.mp4')) return 'video/mp4';
  if (lower.endsWith('.webm')) return 'video/webm';
  if (lower.endsWith('.ogg') || lower.endsWith('.ogv')) return 'video/ogg';
  if (lower.endsWith('.ts')) return 'video/mp2t';
  if (lower.endsWith('.flv')) return 'video/x-flv';
  return 'application/x-mpegURL';
}

function getProxiedUrl(url) {
  if (!url) return url;
  // If stream is HTTP on an HTTPS page, proxy it through the backend
  if (url.startsWith('http://') && window.location.protocol === 'https:') {
    return `${API}/proxy/stream?url=${encodeURIComponent(url)}`;
  }
  return url;
}

export default function Watch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const fr = (i18n.language || "en").startsWith("fr");
  const { settings: siteSettings } = useContext(SettingsContext);
  const membershipEnabled = siteSettings?.membership_enabled !== false;
  const [channel, setChannel] = useState(null);
  const [ads, setAds] = useState([]);
  const [recommendedChannels, setRecommendedChannels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoContainerRef = useRef(null);
  const playerRef = useRef(null);
  const lastTapRef = useRef(0);
  const previewTimerRef = useRef(null);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: "", message: "" });
  const stopViewRef = useRef(null);
  const [error, setError] = useState(null);
  const [previewTimeLeft, setPreviewTimeLeft] = useState(null);
  const [previewExpired, setPreviewExpired] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);

  const isLoggedIn = () => !!localStorage.getItem('user_data');

  const checkFavorite = (channelId) => {
    if (!isLoggedIn()) return;
    axios.get(`${API}/user/favorites/check/${channelId}`, { withCredentials: true })
      .then(res => setIsFavorite(res.data.is_favorite))
      .catch(() => {});
  };

  const toggleFavorite = () => {
    if (!isLoggedIn()) return;
    if (isFavorite) {
      axios.delete(`${API}/user/favorites/${id}`, { withCredentials: true }).then(() => setIsFavorite(false));
    } else {
      axios.post(`${API}/user/favorites/${id}`, {}, { withCredentials: true }).then(() => setIsFavorite(true));
    }
  };

  // Check if user has membership — canonical premium_active (paid OR complimentary OR lifetime)
  const hasMembership = () => {
    try {
      const userData = localStorage.getItem('user_data');
      if (!userData) return false;
      const user = JSON.parse(userData);
      if (user.premium_active) return true;
      if (user.premium_lifetime) return true;
      // Legacy fallback: paid membership_expires in the future
      if (user.has_membership && user.membership_expires) {
        return new Date(user.membership_expires) > new Date();
      }
      return false;
    } catch { return false; }
  };

  // Refresh the authenticated profile so freshly-granted Premium applies without re-login
  useEffect(() => {
    if (!isLoggedIn()) return;
    axios.get(`${API}/user/me`, { withCredentials: true })
      .then(res => { try { localStorage.setItem('user_data', JSON.stringify(res.data)); } catch {} })
      .catch(() => {});
  }, [id]);

  const [denyReason, setDenyReason] = useState(null);

  // Access control: backend is authoritative (returns channel.access with a reason)
  useEffect(() => {
    if (!channel) return;

    if (!membershipEnabled && !channel.access) { setAccessDenied(false); setDenyReason(null); return; }

    // Trust the backend's authoritative access decision when present
    if (channel.access) {
      setAccessDenied(!channel.access.allowed);
      setDenyReason(channel.access.allowed ? null : channel.access.reason);
      return;
    }

    // Fallback (should not normally run): free channels open, premium require membership
    if (channel.is_free === 1) { setAccessDenied(false); return; }
    if (hasMembership()) { setAccessDenied(false); return; }
    setAccessDenied(true); setDenyReason("PREMIUM_REQUIRED");
  }, [channel, membershipEnabled]);

  const handleDoubleTap = () => {
    const container = document.querySelector('[data-testid="video-player"]');
    if (!container) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen || (() => {})).call(document);
    } else {
      const rfs = container.requestFullscreen || container.webkitRequestFullscreen || container.msRequestFullscreen;
      if (rfs) rfs.call(container);
    }
  };

  const handlePlayerTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 350) {
      handleDoubleTap();
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  };

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    
    Promise.all([
      axios.get(`${API}/channels/${id}`),
      axios.get(`${API}/ads`),
      axios.get(`${API}/channels`),
      axios.get(`${API}/settings`),
      axios.post(`${API}/channels/${id}/view`),
    ]).then(([channelRes, adsRes, allChannelsRes, settingsRes]) => {
      const channelData = channelRes.data;
      setChannel(channelData);
      setSettings(settingsRes.data);
      checkFavorite(id);

      // Track channel view
      if (stopViewRef.current) stopViewRef.current();
      stopViewRef.current = trackChannelView(channelData.id, channelData.category_id, channelData.country_id);
      
      // Filter active ads
      const now = new Date();
      const activeAds = adsRes.data.filter((ad) => {
        if (ad.is_active !== 1) return false;
        if (ad.start_date && new Date(ad.start_date) > now) return false;
        if (ad.end_date && new Date(ad.end_date) < now) return false;
        return true;
      });
      setAds(activeAds);
      
      // Get related channels
      if (channelData) {
        const allChannels = allChannelsRes.data;
        const related = allChannels.filter((c) => 
          c.id !== channelData.id && c.category_id === channelData.category_id
        );
        if (related.length < 5) {
          const others = allChannels.filter((c) => 
            c.id !== channelData.id && c.category_id !== channelData.category_id
          );
          related.push(...others.slice(0, 5 - related.length));
        }
        setRecommendedChannels(related.slice(0, 5));
      }
      
      setLoading(false);
    }).catch(err => {
      setError(err.message);
      setLoading(false);
    });

    return () => {
      if (stopViewRef.current) { stopViewRef.current(); stopViewRef.current = null; }
    };
  }, [id]);

  useEffect(() => {
    if (!channel || !videoContainerRef.current) return;

    // If embeddable (YouTube or embed_url), don't use Video.js
    if (isEmbeddable(channel)) return;

    // Only init Video.js for direct stream URLs
    if (!channel.stream_url || playerRef.current) return;

    const videoElement = document.createElement("video");
    videoElement.className = "video-js vjs-big-play-centered vjs-fluid";
    videoElement.setAttribute("playsinline", "true");
    if (channel.logo_url) {
      videoElement.poster = channel.logo_url;
    }
    videoContainerRef.current.appendChild(videoElement);

    const streamType = detectStreamType(channel.stream_url);
    const streamSrc = getProxiedUrl(channel.stream_url);

    playerRef.current = videojs(videoElement, {
      controls: true,
      autoplay: true,
      fluid: true,
      techOrder: ['chromecast', 'html5'],
      plugins: {
        chromecast: {}
      },
      controlBar: {
        children: [
          'playToggle',
          'volumePanel',
          'currentTimeDisplay',
          'timeDivider',
          'durationDisplay',
          'progressControl',
          'remainingTimeDisplay',
          'chromecastButton',
          'pictureInPictureToggle',
          'fullscreenToggle'
        ]
      },
      html5: {
        hls: { overrideNative: true },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
      sources: [{
        src: streamSrc,
        type: streamType
      }]
    });

    playerRef.current.ready(() => {
      if (settings?.player_logo_url) {
        const logoEl = document.createElement('img');
        logoEl.src = settings.player_logo_url;
        logoEl.className = 'absolute z-50 max-w-[100px] max-h-[50px] object-contain pointer-events-none';
        
        const pos = settings.player_logo_position || 'top-right';
        if (pos === 'top-left') logoEl.className += ' top-4 left-4';
        else if (pos === 'top-right') logoEl.className += ' top-4 right-4';
        else if (pos === 'bottom-left') logoEl.className += ' bottom-16 left-4';
        else if (pos === 'bottom-right') logoEl.className += ' bottom-16 right-4';
        else logoEl.className += ' top-4 right-4';
        
        playerRef.current.el().appendChild(logoEl);
      }
    });

    // On error, try fallback type once; if it still fails, surface a stream error state
    let triedFallback = false;
    playerRef.current.on('error', () => {
      const err = playerRef.current.error();
      if (streamType === 'application/x-mpegURL' && !triedFallback) {
        triedFallback = true;
        playerRef.current.src({ src: streamSrc, type: 'video/mp4' });
        playerRef.current.play().catch(() => {});
        return;
      }
      // Distinguish a timeout-ish media error from a generic stream error
      const code = err && err.code;
      setStreamError(code === 2 ? "STREAM_TIMEOUT" : "STREAM_ERROR");
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [channel, settings]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-red-500">
        Error: {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="watch-loading">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-3/4">
            <div className="aspect-video bg-[#0F172A] rounded-xl animate-pulse mb-6"></div>
            <div className="h-8 w-2/3 bg-[#0F172A] rounded animate-pulse mb-4"></div>
          </div>
          <div className="lg:w-1/4">
            <div className="h-6 w-32 bg-[#0F172A] rounded animate-pulse mb-4"></div>
            <div className="flex flex-col gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-32 h-20 bg-[#0F172A] rounded animate-pulse"></div>
                  <div className="flex-1">
                    <div className="h-4 w-full bg-[#0F172A] rounded animate-pulse mb-2"></div>
                    <div className="h-3 w-1/2 bg-[#0F172A] rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <h2 className="text-2xl font-bold mb-4 text-white">Channel not found</h2>
        <Link to="/" className="text-[#94A3B8] hover:text-white underline">{t("watch.returnHome")}</Link>
      </div>
    );
  }

  const aboveAds = hasMembership() ? [] : ads.filter(ad => ad.position === 'above_player');
  const belowAds = hasMembership() ? [] : ads.filter(ad => ad.position === 'below_player');
  const sidebarAds = hasMembership() ? [] : ads.filter(ad => ad.position === 'sidebar');

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Watch ${channel.name} on DJILU.COM`,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setAlertModal({
        isOpen: true,
        title: "Success",
        message: "Link copied to clipboard!"
      });
    }
  };

  const renderAd = (ad) => (
    <div key={ad.id} className="w-full bg-[#0F172A] rounded-xl overflow-hidden flex justify-center border border-white/[0.06]">
      {ad.type === 'image' ? (
        ad.target_url ? (
          <a href={ad.target_url} target="_blank" rel="noopener noreferrer" className="block w-full">
            <img src={ad.content} alt={ad.name} className="w-full h-auto" referrerPolicy="no-referrer" />
          </a>
        ) : (
          <img src={ad.content} alt={ad.name} className="max-w-full h-auto" referrerPolicy="no-referrer" />
        )
      ) : (
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ad.content, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src'] }) }} className="w-full" />
      )}
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8" data-testid="watch-page">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-2 text-sm text-[#94A3B8] mb-6">
        <button onClick={() => navigate(-1)} className="hover:text-white transition-colors" data-testid="back-btn">{t("watch.back")}</button>
        <ChevronRight size={14} />
        <span className="text-white font-medium">{channel.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          {/* Ads Above Player */}
          {aboveAds.length > 0 && <div className="space-y-4">{aboveAds.map(renderAd)}</div>}

          {/* Player Container */}
          <div 
            className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl relative group border border-white/[0.06]" 
            data-testid="video-player"
            onDoubleClick={handleDoubleTap}
            onTouchEnd={handlePlayerTap}
          >
            {/* Access denied — premium required */}
            {accessDenied && membershipEnabled && denyReason === "PREMIUM_REQUIRED" && (
              <div className="absolute inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center" data-testid="access-denied">
                <CreditCard size={48} className="text-blue-400 mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">{fr ? "Chaîne Premium" : "Premium Channel"}</h2>
                <p className="text-[#94A3B8] text-sm mb-6 max-w-sm">{t("watch.premiumRequired")}</p>
                <div className="flex gap-3">
                  <Link to="/membership" className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-colors shadow-lg shadow-blue-600/20">
                    {fr ? "Voir les offres" : "View Plans"}
                  </Link>
                  <Link to="/account" className="px-6 py-3 bg-[#172033] text-white rounded-xl font-bold hover:bg-[#1e293b] transition-colors border border-white/[0.06]">
                    {fr ? "Se connecter" : "Sign In"}
                  </Link>
                </div>
              </div>
            )}

            {/* Account suspended */}
            {accessDenied && denyReason === "ACCOUNT_SUSPENDED" && (
              <div className="absolute inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center" data-testid="account-suspended">
                <AlertTriangle size={48} className="text-red-400 mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">{fr ? "Compte suspendu" : "Account Suspended"}</h2>
                <p className="text-[#94A3B8] text-sm max-w-sm">{fr ? "Votre compte est actuellement suspendu. Contactez l'assistance." : "Your account is currently suspended. Please contact support."}</p>
              </div>
            )}

            {/* Channel unavailable / disabled / stream error */}
            {((accessDenied && (denyReason === "CHANNEL_TEMPORARILY_UNAVAILABLE" || denyReason === "CHANNEL_DISABLED")) || streamError) && (
              <div className="absolute inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center" data-testid="channel-unavailable">
                <WifiOff size={48} className="text-amber-400 mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">
                  {fr ? "Cette chaîne n'est pas disponible pour le moment." : "This channel is currently unavailable."}
                </h2>
                <p className="text-[#94A3B8] text-sm mb-6 max-w-sm">
                  {fr ? "Veuillez revenir plus tard ou réessayer dans quelques instants." : "Please check back later or try again shortly."}
                </p>
                <div className="flex gap-3 flex-wrap justify-center">
                  <button onClick={() => { setStreamError(null); window.location.reload(); }} data-testid="retry-btn"
                    className="px-5 py-2.5 bg-amber-500 text-black rounded-xl font-bold hover:bg-amber-400 transition-colors">
                    {fr ? "Réessayer" : "Try Again"}
                  </button>
                  <Link to="/live-tv" data-testid="back-to-channels-btn"
                    className="px-5 py-2.5 bg-[#172033] text-white rounded-xl font-bold hover:bg-[#1e293b] transition-colors border border-white/[0.06]">
                    {fr ? "Retour aux chaînes" : "Back to Channels"}
                  </Link>
                  <Link to="/report" data-testid="report-problem-btn"
                    className="px-5 py-2.5 bg-[#172033] text-white rounded-xl font-bold hover:bg-[#1e293b] transition-colors border border-white/[0.06]">
                    {fr ? "Signaler un problème" : "Report a Problem"}
                  </Link>
                </div>
              </div>
            )}

            {/* Free channel badge */}
            {membershipEnabled && channel.is_free === 1 && (
              <div className="absolute top-3 right-3 z-40 bg-emerald-600/80 backdrop-blur-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5" data-testid="free-badge">
                <span className="text-white text-xs font-bold">FREE</span>
              </div>
            )}

            {isEmbeddable(channel) ? (
              isRawIframe(channel.embed_url) ? (
                <div
                  className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(channel.embed_url, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src'] }) }}
                  data-testid="embed-raw-iframe"
                />
              ) : (
                <iframe
                  src={getEmbedSrc(channel)}
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  frameBorder="0"
                  title={channel.name}
                  data-testid="embed-iframe"
                />
              )
            ) : (
              <div ref={videoContainerRef} className="w-full h-full" />
            )}
          </div>

          {/* Ad: below the player */}
          <AdSlot placement="player_below" channel={channel?.id || ""} className="my-4" />

          {/* Channel Info */}
          <div className="bg-[#0F172A] rounded-2xl p-6 border border-white/[0.06]">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[#050814] rounded-xl border border-white/[0.06] p-2 flex items-center justify-center">
                  <img src={channel.logo_url} alt={channel.name} className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white" data-testid="channel-name">{channel.name}</h1>
                  <div className="flex items-center gap-4 mt-1 text-sm text-[#94A3B8]">
                    <Link to="/" state={{ country: channel.country_id }} className="flex items-center gap-1 hover:text-white transition-colors">
                      <Globe size={14} />
                      {channel.country_name}
                    </Link>
                    <Link to="/" state={{ category: channel.category_id }} className="px-2 py-0.5 rounded bg-[#FFC928]/10 text-[#FFC928] border border-[#FFC928]/20 font-medium hover:bg-[#FFC928]/20 transition-colors">
                      {channel.category_name}
                    </Link>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-[#172033] rounded-lg text-[#94A3B8]">
                  <Eye size={18} />
                  <span className="font-bold" data-testid="view-count">{channel.views}</span>
                </div>
                <button onClick={handleShare} data-testid="share-btn" className="p-2.5 rounded-lg border border-white/[0.06] text-[#94A3B8] hover:text-white hover:bg-white/[0.05] transition-colors">
                  <Share2 size={20} />
                </button>
                {isLoggedIn() && (
                  <button onClick={toggleFavorite} data-testid="favorite-btn" className={`p-2.5 rounded-lg border transition-colors ${isFavorite ? 'border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/20' : 'border-white/[0.06] text-[#94A3B8] hover:text-red-400 hover:bg-white/[0.05]'}`}>
                    <Heart size={20} fill={isFavorite ? 'currentColor' : 'none'} />
                  </button>
                )}
              </div>
            </div>
            {channel.description && (
              <p className="text-sm text-[#94A3B8] mt-3 leading-relaxed" data-testid="channel-description">{channel.description}</p>
            )}
          </div>

          {/* Ads Below Player */}
          {belowAds.length > 0 && <div className="space-y-4">{belowAds.map(renderAd)}</div>}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-[#0F172A] rounded-2xl border border-white/[0.06] overflow-hidden">
            <div className="p-4 border-b border-white/[0.06] bg-[#050814]/50">
              <h3 className="font-bold flex items-center gap-2 text-white">
                <Tv size={18} className="text-[#FFC928]" />
                {t("watch.relatedChannels")}
              </h3>
            </div>
            <div className="p-4 space-y-3" data-testid="related-channels">
              {recommendedChannels.length > 0 ? (
                recommendedChannels.map(recChannel => (
                  <Link 
                    key={recChannel.id} 
                    to={`/watch/${recChannel.id}`}
                    data-testid={`related-channel-${recChannel.id}`}
                    className="flex gap-3 group hover:bg-white/[0.05] p-2 rounded-xl transition-colors"
                  >
                    <div className="w-24 h-16 bg-[#050814] rounded-lg border border-white/[0.06] p-1 flex-shrink-0 relative overflow-hidden">
                      <img 
                        src={recChannel.logo_url || "https://picsum.photos/seed/tv/400/300"} 
                        alt={recChannel.name} 
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300" 
                        referrerPolicy="no-referrer" 
                      />
                    </div>
                    <div className="flex flex-col justify-center overflow-hidden">
                      <h4 className="font-bold text-sm text-white truncate group-hover:text-[#FFC928] transition-colors">
                        {recChannel.name}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-[#94A3B8] mt-1">
                        <span className="truncate">{recChannel.category_name}</span>
                        <span className="w-1 h-1 rounded-full bg-white/10"></span>
                        <span className="flex items-center gap-1 flex-shrink-0">
                          <Eye size={10} />
                          {recChannel.views}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <p className="text-sm text-[#94A3B8] text-center py-8">
                  {t("watch.noRelated")}
                </p>
              )}
            </div>
          </div>

          {/* Sidebar Ads */}
          {sidebarAds.length > 0 && <div className="space-y-4">{sidebarAds.map(renderAd)}</div>}
        </div>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
